import { Button, IconButton, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useEffect, useState } from 'react'
import { TextSnippetOutlined, ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material'
import DocumentsCarouselView from '../document/DocumentsCarouselView'
import { SmartTextArea, useSmartTextArea } from '../../generic/smarttextarea/SmartTextArea'

/**
 * A section with a header that toggles the visibility of its content. When
 * collapsed only the header shows, so sibling flex content (the image) grows.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {boolean} props.collapsed
 * @param {Function} props.onToggle
 * @param {React.ReactNode} props.children
 */
const CollapsibleSection = ({ title, collapsed, onToggle, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: collapsed ? '0 0 auto' : '1 1 0', minHeight: 0 }}>
    <div
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', userSelect: 'none' }}
    >
      <IconButton size='small' aria-label={title} tabIndex={-1}>
        {collapsed ? <ExpandMoreRounded fontSize='small' /> : <ExpandLessRounded fontSize='small' />}
      </IconButton>
      <Typography variant='subtitle2' color='primary'>{title}</Typography>
    </div>
    {!collapsed && <div style={{ flex: '1 1 0', minHeight: 0 }}>{children}</div>}
  </div>
)

const TaskInstruction = (props) => {
  const { task, getTempUUID, editing, forceReloadDocuments, openDocuments, editor, notesEditor, showDocumentsHeader, balancedStyle = false, collapsed = { instructions: false, notes: false }, onToggleCollapse = () => {} } = props

  const { t_ } = useSmartTranslation()

  const image = (
    <DocumentsCarouselView
      resourceType='TASK'
      referenceUUID={task?.uuid}
      referenceTempUUID={getTempUUID ? getTempUUID(task?.uuid) : undefined}
      referenceId={task?.id}
      forceReloadDocuments={forceReloadDocuments}
      renderCustomHeader={openDocuments ? <Button variant='outlined' onClick={openDocuments} startIcon={<TextSnippetOutlined />}>{t_('Documents')}</Button> : null}
      showDocumentsHeader={showDocumentsHeader}
      thumbnailsCollapsed={balancedStyle ? !!collapsed.documents : undefined}
      onToggleThumbnails={balancedStyle ? () => onToggleCollapse('documents') : undefined}
      enableUpload={editing}
      enableReorder={editing}
    />
  )

  // Balanced editor: image on top (grows, thumbnails collapse inside it), Instructions
  // and Notes collapsible below.
  if (balancedStyle) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', gap: '0.5rem' }}>
        <div data-testid='task-image' style={{ flex: '1 1 0', minHeight: '8rem' }}>
          {image}
        </div>
        <CollapsibleSection title={t_('Instructions')} collapsed={!!collapsed.instructions} onToggle={() => onToggleCollapse('instructions')}>
          <div data-testid='instructions-content' style={{ height: '100%' }}>
            <SmartTextArea editor={editor} readOnly={!editing} />
          </div>
        </CollapsibleSection>
        <CollapsibleSection title={t_('Notes')} collapsed={!!collapsed.notes} onToggle={() => onToggleCollapse('notes')}>
          <div data-testid='notes-content' style={{ height: '100%' }}>
            <SmartTextArea editor={notesEditor} readOnly={!editing} />
          </div>
        </CollapsibleSection>
      </div>
    )
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '3fr 2fr',
      gridTemplateRows: '3fr 2fr',
      gap: '1rem',
      height: '100%',
      width: '100%'
    }}
    >
      <div style={{ gridRow: '1 / span 2', minWidth: 0 }}>
        {image}
      </div>
      <div style={{ minHeight: 0, height: '100%' }}>
        <SmartTextArea title={t_('Instructions')} editor={editor} readOnly={!editing} />
      </div>
      <SmartTextArea title={t_('Notes')} editor={notesEditor} readOnly={!editing} />
    </div>
  )
}

const TaskInstructionDialog = (props) => {
  const { task, updateTask, open, closeCallback, openDocuments, getTempUUID, editing, title, forceReloadDocuments } = props
  const [instructions, setInstructions] = useState('')
  const [notes, setNotes] = useState('')
  const instructionsEditor = useSmartTextArea(instructions)
  const notesEditor = useSmartTextArea(notes)

  useEffect(() => {
    if (!open) {
      return
    }
    setInstructions(task?.instructions)
    setNotes(task?.notes)
  }, [open, task])

  const onClose = () => {
    const newInstructions = instructionsEditor.getData()
    const newNotes = notesEditor.getData()
    if (newInstructions !== task?.instructions || newNotes !== task?.notes) {
      updateTask({ ...task, instructions: newInstructions, notes: newNotes })
    }
    instructionsEditor.resetInitialContent()
    notesEditor.resetInitialContent()
    setInstructions('')
    setNotes('')
    closeCallback()
  }

  return (
    <SmartDialog
      setOpen={open}
      renderComponent={
        <TaskInstruction
          task={task}
          openDocuments={openDocuments}
          getTempUUID={getTempUUID}
          editing={editing}
          forceReloadDocuments={forceReloadDocuments}
          editor={instructionsEditor.editor}
          notesEditor={notesEditor.editor}
        />
    }
      minWidth='calc(100vw - 64px)'
      paperHeight='calc(100vh - 64px)'
      growContainerHeight
      cancel={false}
      accept={false}
      title={createDialogTitle({ color: 'primary', title })}
      close
      closeCallback={onClose}
    />
  )
}

export { TaskInstructionDialog, TaskInstruction }
